/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/**
 * Loads a stylesheet.
 * @param {string} href - Url of stylesheet to load.
 * @returns {Promise}
 */
function loadStylesheet(href) {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve(link);
    link.onerror = () => reject;
    document.head.appendChild(link);
  });
}

/* global Modal, Swiper */

customElements.whenDefined('modal-dialog').then(() => {
  if (!customElements.get('video-testimonials')) {
    class VideoTestimonials extends Modal {
      connectedCallback() {
        this.pauseBtn = this.querySelector('.pause-btn');
        this.muteBtn = this.querySelector('.mute-btn');
        this._playTarget = null;

        this.pauseBtn?.addEventListener('click', this.pauseActiveVideo.bind(this));
        this.muteBtn?.addEventListener('click', this.muteAllVideos.bind(this));

        // Direct listener on close button — independent of event bubbling
        this.querySelectorAll('.js-close-modal').forEach((btn) => {
          btn.addEventListener('click', (evt) => {
            evt.stopPropagation();
            this.close();
          });
        });
      }

      open(opener) {
        super.open(opener);
        const { videoId } = opener.querySelector('video-thumbnail').dataset;

        if (!this.swiper) {
          // Play target video immediately to begin buffering and establish user gesture intent.
          // playVideo() uses a mute-then-unmute trick so iOS Safari allows autoplay.
          const targetSlide = this.querySelector(`.swiper-slide:not(.theme-clone)[data-video-id="${videoId}"]`);
          const targetVideo = targetSlide?.querySelector('video');
          if (targetVideo) {
            this.pauseAllVideos(targetVideo);
            this.playVideo(targetVideo);
          }

          if (theme.swiperLoaded) {
            this.initSwiper(videoId);
          } else {
            Promise.all([
              loadScript(this.dataset.swiperJsPath),
              loadStylesheet(this.dataset.swiperCssPath)
            ]).then(() => {
              theme.swiperLoaded = true;
              this.initSwiper(videoId);
            });
          }
        } else {
          const videoIndex = parseInt(this.querySelector(`.swiper-slide:not(.theme-clone)[data-video-id="${videoId}"]`).dataset.swiperSlideIndex, 10);
          this.swiper.slideToLoop(videoIndex, 0, false);
          const activeVideo = this.swiper.slides[this.swiper.activeIndex].querySelector('video');
          if (activeVideo) {
            this.pauseAllVideos(activeVideo);
            this.playVideo(activeVideo);
            this.updateControls(activeVideo);
          }
        }

        this.stickyAtc = this.closest('.shopify-section').querySelector('sticky-atc-panel');
        this.stickyAtc?.videoTestimonialsOpened();
      }

      close() {
        this._playTarget = null;
        this.pauseAllVideos();
        super.close();
        this.stickyAtc?.videoTestimonialsClosed();
      }

      // Plays a video using a mute-then-unmute sequence so that the play() call is
      // never rejected by iOS Safari's autoplay policy (muted autoplay is always
      // permitted). The original mute state is restored once playback actually starts.
      // _playTarget guards against stale callbacks when the user has swiped away.
      playVideo(video) {
        if (!video) return;
        this._playTarget = video;
        const wasMuted = video.muted;
        video.muted = true;
        video.play()
          .then(() => {
            if (this._playTarget === video && this.hasAttribute('open')) {
              video.muted = wasMuted;
              this.updateControls(video);
            }
          })
          .catch(() => {
            video.muted = wasMuted;
          });
      }

      // Kicks off loading for the active slide and its immediate neighbours
      preloadAdjacentVideos(swiper) {
        [swiper.activeIndex - 1, swiper.activeIndex, swiper.activeIndex + 1].forEach((i) => {
          const slide = swiper.slides[i];
          if (!slide) return;
          const v = slide.querySelector('video');
          if (v && v.readyState === 0) v.load();
        });
      }

      initSwiper(videoId) {
        const slides = [...this.querySelectorAll('.swiper-slide')];
        let videoIndex = 0;
        slides.forEach((el, index) => {
          if (el.dataset.videoId === videoId) {
            videoIndex = index;
          }
          // Duplicate slides if more than one exists as more are needed for Swiper's loop effect
          if (slides.length > 1) {
            el.parentElement.insertAdjacentHTML('beforeend', el.outerHTML);
            el.parentElement.lastElementChild.classList.add('theme-clone');
          }
        });

        this.swiper = new Swiper(`#${this.id} .swiper--testimonial-videos`, {
          effect: 'coverflow',
          initialSlide: videoIndex,
          grabCursor: true,
          centeredSlides: true,
          slidesPerView: 3,
          spaceBetween: 20,
          breakpoints: {
            768: {
              slidesPerView: 3,
              spaceBetween: 0
            }
          },
          coverflowEffect: {
            rotate: 0,
            stretch: 0,
            depth: 400,
            modifier: 1,
            slideShadows: false
          },
          loop: true,

          navigation: {
            nextEl: `#${this.id} .swiper--testimonial-videos--next`,
            prevEl: `#${this.id} .swiper--testimonial-videos--prev`
          },
          a11y: {
            slideRole: null
          },

          on: {
            // Fires immediately when slide starts changing — pause the outgoing video
            // right away so there is no audio overlap during the swipe animation.
            slideChange: (swiper) => {
              const incoming = swiper.slides[swiper.activeIndex].querySelector('video');
              this.pauseAllVideos(incoming);
            },
            // Fires after the swipe animation ends — safe to start the incoming video.
            slideChangeTransitionEnd: (swiper) => {
              const video = swiper.slides[swiper.activeIndex].querySelector('video');
              if (this.hasAttribute('open')) {
                this.preloadAdjacentVideos(swiper);
                this.playVideo(video);
              } else {
                this.pauseAllVideos();
              }
              this.updateControls(video);
            },
            afterInit: (swiper) => {
              this.preloadAdjacentVideos(swiper);
              const video = swiper.slides[swiper.activeIndex].querySelector('video');
              if (video && this.hasAttribute('open')) {
                // Pause everything except the Swiper active slide's video (handles the case
                // where open() played a different element before Swiper created its clones).
                this.pauseAllVideos(video);
                // If the video is still paused (was a different element from what open()
                // started, or open() play failed), start it now. playVideo() handles iOS
                // autoplay via mute trick so this works in both sync and async Swiper init.
                if (video.paused) this.playVideo(video);
              }
              setTimeout(() => this.updateControls(video), 250);
            }
          }
        });
      }

      pauseActiveVideo() {
        const video = this.swiper.slides[this.swiper.activeIndex].querySelector('video');
        if (VideoTestimonials.isVideoPlaying(video)) {
          this._playTarget = null;
          video.pause();
        } else {
          this._playTarget = video;
          video.play().catch(() => {});
        }
        this.updateControls(video);
      }

      muteAllVideos() {
        const video = this.swiper.slides[this.swiper.activeIndex].querySelector('video');
        if (video.muted) {
          this.querySelectorAll('video').forEach((v) => { v.muted = false; });
        } else {
          this.querySelectorAll('video').forEach((v) => { v.muted = true; });
        }
        this.updateControls(video);
      }

      updateControls(videoParam) {
        const video = videoParam || this.swiper.slides[this.swiper.activeIndex].querySelector('video');
        const playing = VideoTestimonials.isVideoPlaying(video);

        this.pauseBtn.querySelector('.pause-btn__pause-icon').toggleAttribute('hidden', !playing);
        this.pauseBtn.querySelector('.pause-btn__play-icon').toggleAttribute('hidden', playing);

        this.muteBtn.querySelector('.mute-btn__mute-icon').toggleAttribute('hidden', video.muted);
        this.muteBtn.querySelector('.mute-btn__unmute-icon').toggleAttribute('hidden', !video.muted);
      }

      pauseAllVideos(except) {
        this.querySelectorAll('video').forEach((video) => {
          if (video !== except) video.pause();
        });
      }

      static isVideoPlaying(video) {
        return video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2;
      }
    }

    customElements.define('video-testimonials', VideoTestimonials);
  }
});
